import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CitedText } from "../assistant/AgentDesk";
import { aiChatService } from "../../services/api";
import { citationSources } from '../common/AssistantMarkdown';

export default function ChatResearch({ results = [], timezone, compact = false }) {
  const client = useQueryClient();
  const [saved, setSaved] = useState({});
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState({});
  const date = value => new Date(value).toLocaleString(undefined, { timeZone: timezone || "UTC", dateStyle: "medium", timeStyle: "short" });
  async function capture(result) {
    if (busy) return;
    setBusy(result.id); setError({});
    try {
      const note = await aiChatService.captureResearch(result.id, { expected_revision: result.revision });
      setSaved(current => ({...current, [result.id]: note.id}));
      await client.invalidateQueries({queryKey:["notes"]});
    } catch (failure) {
      setError({[result.id]: typeof failure.response?.data?.detail === "string" ? failure.response.data.detail : "Could not save this research. Retry to recover the existing note if the response was lost."});
    } finally { setBusy(null); }
  }
  if (!results.length) return null;
  return (
    <section className="mt-4 space-y-3" aria-label="Live research results">
      {results.map(result => {
        const expired = result.expired || (result.expires_at && Date.parse(result.expires_at) <= Date.now());
        const noteId = saved[result.id] || result.note_id;
        const complete = result.status === "completed" && !!result.content && !!result.citations?.length;
        const sources = citationSources(result.citations);
        return (
          <article key={result.id} className="min-w-0 rounded-xl border border-[#d9e3dc] bg-[#f5f8f3] p-4 space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#526759]">{complete ? expired ? "Past research · refresh before relying on availability" : "Researched with web sources" : `Research ${result.status.replaceAll("_", " ")}`}</p>
              <h3 className="mt-1 font-semibold">{result.query}</h3>
              {result.location && <p className="text-xs">Location: {result.location}</p>}
              {result.researched_at && <p className="mt-1 text-xs text-[#66716a]">Researched {date(result.researched_at)} · Freshness ends {date(result.expires_at)}</p>}
            </div>
            {complete ? <>
              <div className="flex flex-wrap gap-2" aria-label="Research sources">
                {sources.map(source => <a key={source.url} href={source.url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-start gap-2 rounded-lg border border-[#d5e2d9] bg-white px-3 py-2 text-xs font-medium text-[#246b52] hover:bg-[#edf4ee] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#246b52]">
                  <span aria-hidden="true" className="shrink-0 font-bold">{source.number}</span><span className="break-words">{source.title || new URL(source.url).hostname}</span>
                </a>)}
              </div>
              <details open={!compact} className="group border-t border-[#dce6df] pt-3">
                <summary className="cursor-pointer text-sm font-semibold text-[#365b47] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#246b52]">Research details</summary>
                <div className="mt-3"><CitedText content={result.content} citations={result.citations} /></div>
              </details>
              {noteId ? <Link className="text-sm underline" to={`/notes?note=${noteId}`}>Open research note</Link> : <button className="secondary-button text-xs" disabled={!!busy} onClick={() => capture(result)}>{busy === result.id ? "Saving…" : "Keep research in Notes"}</button>}
              <p className="text-xs text-[#66716a]">Findings keep their source links and research date. Ask for a new search to check changing prices, dates or availability.</p>
            </> : <p role="status" className="text-sm">{result.error || "This search is still being checked. Use Check saved status to recover its result."}</p>}
            {error[result.id] && <p role="alert" className="text-sm text-[#a04434]">{error[result.id]}</p>}
          </article>
        );
      })}
    </section>
  );
}
